/**
 * Application Bootstrap Utilities
 * Handles initial configuration loading and session setup
 */

import {
  getEnvironments,
  getNavigation,
  setEnvironmentGlobal,
  createSession,
  setSessionId,
  setAuthType,
  getSessionId,
  getAuthType,
} from '../api';
import { ProgramInformation, NavigationConfig } from '../api/types';
import { createLogger } from './logging';

const OHID = 'ohid';
const TESTING = 'testing';
const CODE = 'code';
const logger = createLogger('OAuthCallback');

/**
 * Load and initialize environment configuration
 */
export async function initializeEnvironment(): Promise<ProgramInformation> {
  const env = await getEnvironments();
  setEnvironmentGlobal(env);
  return env;
}

/**
 * Load navigation configuration
 */
export async function initializeNavigation(): Promise<NavigationConfig> {
  const nav = await getNavigation();
  (window as any)['navigation'] = nav;
  return nav;
}

/**
 * Setup no-auth mode for local development
 */
export function setupNoAuthMode(): void {
  const environments = (window as any)['environments'];
  
  if (environments?.no_auth === true) {
    alert('WARNING! NextGen is running without authentication!');
    setAuthType(TESTING);
    setSessionId(TESTING);
  }
}

/**
 * Setup OHID auto-login from client portal redirect
 */
export function setupClientPortalAutoLogin(): void {
  const query = new URLSearchParams(window.location.search);
  const prompt = query.get('prompt');
  const code = query.get(CODE);
  
  // User has come from another application and is already signed in with OHID
  // Suppress the first sign-on screen
  if (prompt) {
    const hasSession = !!getSessionId(); // Use truthiness check instead of !== null
    
    if (!hasSession && !code) {
      // Set auth type to OHID for auto-login
      setAuthType(OHID);
    }
  }
}

/**
 * Initialize default auth type if not set
 */
export function initializeDefaultAuthType(): void {
  const authType = getAuthType();
  
  if (!authType) {
    setAuthType(OHID);
  }
  
  // Store in localStorage for legacy compatibility
  const currentAuthType = getAuthType();
  if (currentAuthType) {
    localStorage.setItem('loginType', currentAuthType);
  }
}

/**
 * Handle OAuth callback with authorization code
 */
export function hasBenefitsCentralAccountState(state: string | null): boolean {
  if (!state) {
    return false;
  }

  const hasAccountId = (value: string): boolean => {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed?.accountId === 'string' && parsed.accountId.length > 0) {
        return true;
      }
    } catch (_) {
      // The current Benefits Central state is query-string shaped.
    }

    return Boolean(new URLSearchParams(value).get('accountId'));
  };

  let value = state;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (hasAccountId(value)) {
      return true;
    }

    try {
      const decoded = decodeURIComponent(value.replace(/\+/g, ' '));
      if (decoded === value) {
        break;
      }
      value = decoded;
    } catch (_) {
      break;
    }
  }

  try {
    return hasAccountId(atob(state.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (_) {
    return false;
  }
}

export function getOAuthCallbackAuthType(state: string | null, storedAuthType: string): string {
  // Benefits Central is the only non-NextGen launch and always includes its
  // selected account in state. This fixes stale MSID browser storage without
  // changing the IdP redirect URI.
  return hasBenefitsCentralAccountState(state) ? OHID : storedAuthType;
}

export async function handleOAuthCallback(code: string, authType: string): Promise<string> {
  const startedAt = Date.now();
  sessionStorage.removeItem('logout');

  if (!authType) {
    throw new Error('OAuth callback has no declared authentication type');
  }

  // Always overwrite stale browser state. The callback route, not a prior
  // session, is authoritative for the authentication flow being completed.
  setAuthType(authType);

  const state = new URLSearchParams(window.location.search).get('state');
  const benefitsCentralLaunch = hasBenefitsCentralAccountState(state);
  logger.info('Callback started', {
    authType,
    callbackPath: window.location.pathname,
    benefitsCentralLaunch,
    statePresent: Boolean(state),
  });

  try {
    const response = benefitsCentralLaunch
      ? await createSession(code, authType, state ?? undefined, true)
      : state
        ? await createSession(code, authType, state)
        : await createSession(code, authType);

    setSessionId(response.sessionId);
    logger.info('Callback completed', {
      authType,
      callbackPath: window.location.pathname,
      benefitsCentralLaunch,
      durationMs: Date.now() - startedAt,
    });
    return response.sessionId;
  } catch (error) {
    logger.error('Callback failed', {
      authType,
      callbackPath: window.location.pathname,
      benefitsCentralLaunch,
      statePresent: Boolean(state),
      durationMs: Date.now() - startedAt,
      error: error instanceof Error ? error.message : 'unknown error',
    });
    throw error;
  }
}

/**
 * Determine which component to render based on auth state
 */
export function getBootstrapState(): 'app' | 'login' {
  const query = new URLSearchParams(window.location.search);
  const code = query.get(CODE);
  const sessionId = getSessionId();
  const isLogout = sessionStorage.getItem('logout');
  
  // Check if user is logging out
  if (isLogout) {
    sessionStorage.removeItem('logout');
    return 'login';
  }
  
  // Check if user has valid session
  if (sessionId) {
    return 'app';
  }
  
  // Check if user is returning from OAuth provider
  if (code) {
    return 'app'; // Will trigger OAuth callback
  }
  
  // No session, no code = show login
  return 'login';
}

/**
 * Check if authentication is enabled
 */
export function isAuthEnabled(): boolean {
  const nav = (window as any)['navigation'];
  return nav?.authentication === 'auth-proxy';
}
